import { z } from 'zod'

// Schema para materiais disponíveis (PDFs, vídeos, etc)
export const MaterialSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  type: z.enum(['pdf', 'video', 'audio', 'document', 'image', 'link']),
  url: z.string().url(),
  thumbnail: z.string().url().optional(),
  size: z.string().optional(), // Ex: "2.5 MB"
  duration: z.string().optional(), // Para vídeos/áudios: "45:30"
  courseName: z.string().optional(),
  uploadedAt: z.date(),
  downloadCount: z.number().min(0).optional(),
  category: z.string().optional()
})

// Schema para turmas
export const ClassSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  thumbnail: z.string().url().optional(),
  instructor: z.object({
    id: z.string(),
    name: z.string(),
    avatar: z.string().url().optional()
  }),
  startDate: z.date(),
  endDate: z.date().optional(),
  schedule: z.string(), // Ex: "Terças e Quintas, 19h-21h"
  studentsCount: z.number().min(0),
  maxStudents: z.number().optional(),
  status: z.enum(['active', 'upcoming', 'completed', 'cancelled']),
  progress: z.number().min(0).max(100).optional(),
  nextClass: z.date().optional(),
  platform: z.string().optional(), // Ex: "Zoom", "Google Meet"
  meetingLink: z.string().url().optional(),
  category: z.string().optional()
})

// Schema para configurações de conta
export const AccountSettingsSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('Email inválido'),
  avatar: z.string().url().optional(),
  phone: z.string().optional(),
  bio: z.string().max(500, 'Biografia deve ter no máximo 500 caracteres').optional(),
  notifications: z.object({
    email: z.boolean(),
    push: z.boolean(),
    sms: z.boolean()
  }).optional(),
  privacy: z.object({
    profileVisible: z.boolean(),
    showProgress: z.boolean()
  }).optional()
})

// Schema para alteração de senha
export const ProfileChangePasswordSchema = z.object({
  currentPassword: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
  newPassword: z.string().min(6, 'Nova senha deve ter pelo menos 6 caracteres'),
  confirmPassword: z.string()
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'As senhas não coincidem',
  path: ['confirmPassword']
})

// Schema para estatísticas do perfil
export const ProfileStatsSchema = z.object({
  coursesCompleted: z.number().min(0),
  coursesInProgress: z.number().min(0),
  totalStudyTime: z.number().min(0), // em minutos
  certificatesEarned: z.number().min(0),
  currentStreak: z.number().min(0), // dias consecutivos
  totalClasses: z.number().min(0),
  materialsDownloaded: z.number().min(0),
  totalProducts: z.number().min(0), // total de produtos/cursos adquiridos
  activeProducts: z.number().min(0), // produtos ativos
  completedProducts: z.number().min(0) // produtos concluídos
})

// Type exports
export type Material = z.infer<typeof MaterialSchema>
export type Class = z.infer<typeof ClassSchema>
export type AccountSettings = z.infer<typeof AccountSettingsSchema>
export type ProfileChangePassword = z.infer<typeof ProfileChangePasswordSchema>
export type ProfileStats = z.infer<typeof ProfileStatsSchema>
